import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { toBatchFilterSearch, toBatchFilterValues } from '../utils/publishFilters';

const ROUTES = {
  sales: getRouteApi('/_auth/publish/sales'),
  mis: getRouteApi('/_auth/publish/mis'),
  accounts: getRouteApi('/_auth/publish/accounts'),
} as const;

export type BatchPanelStage = keyof typeof ROUTES;

const normalizeFilterValues = (values: Record<string, string>) =>
  toBatchFilterValues(toBatchFilterSearch(values));

export function useBatchFilters(stage: BatchPanelStage) {
  const route = ROUTES[stage];
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toBatchFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toBatchFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const applySearchText = useCallback(
    (text: string | undefined) => {
      void navigate({ search: (previous) => ({ ...previous, search: text }), replace: true });
    },
    [navigate],
  );

  return { ...filters, search, appliedSearchText: search.search, applySearchText };
}
